/**
 * Owner Rewards — card grid + Create/Edit drawer.
 */
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Gift, Edit, Power, Trash2, Stamp } from 'lucide-react';
import { toast } from 'sonner';
import { api, resolveImageUrl } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';
import { formatDate } from '@/lib/utils';
import {
  PageHeader, SectionCard, FullSpinner, QueryError, EmptyState, StatusPill,
} from '@/components/ui_primitives/Misc';
import Button from '@/components/ui_primitives/Button';
import Drawer from '@/components/ui_primitives/Drawer';
import { FieldRow, Input, Textarea } from '@/components/ui_primitives/Field';
import ImageUploader from '@/components/ui_primitives/ImageUploader';
import { confirm } from '@/components/ui_primitives/confirm';

const schema = z.object({
  title: z.string().min(1).max(120),
  description: z.string().min(1).max(500),
  stamps_required: z.coerce.number().int().min(1).max(1000),
  expiry_date: z.string().optional().or(z.literal('')),
  terms_and_conditions: z.string().max(2000).optional().or(z.literal('')),
  available_branch_ids: z.array(z.string()).default([]),
});

export default function OwnerRewardsPage() {
  const token = useAuthStore((s) => s.token);
  const brandId = useAuthStore((s) => s.user?.brand_id);
  const qc = useQueryClient();
  const [editing, setEditing] = useState(null);

  const rewardsQ = useQuery({
    queryKey: qk.ownerRewards(brandId),
    queryFn: () => api.ownerRewards(token),
    enabled: !!token && !!brandId,
  });
  const branchesQ = useQuery({
    queryKey: qk.ownerBranches(brandId),
    queryFn: () => api.ownerBranches(token),
    enabled: !!token && !!brandId,
  });

  const invalidate = () => qc.invalidateQueries({ queryKey: qk.ownerRewards(brandId) });
  const toggle = useMutation({
    mutationFn: ({ id, on }) => on ? api.ownerActivateReward(token, id) : api.ownerDeactivateReward(token, id),
    onSuccess: (_d, v) => { toast.success(v.on ? 'Reward activated' : 'Reward deactivated'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Action failed')),
  });
  const remove = useMutation({
    mutationFn: (id) => api.ownerDeleteReward(token, id),
    onSuccess: () => { toast.success('Reward deleted'); invalidate(); },
    onError: (e) => toast.error(String(e?.detail ?? 'Delete failed')),
  });

  if (rewardsQ.isLoading) return <FullSpinner />;
  if (rewardsQ.error) return <QueryError error={rewardsQ.error} onRetry={() => rewardsQ.refetch()} />;

  const items = (rewardsQ.data || []).filter((r) => !r.deleted_at);

  return (
    <div className="space-y-6" data-testid="owner-rewards-page">
      <PageHeader
        kicker="Rewards"
        title="Reward catalogue"
        subtitle="Set the rewards customers unlock with stamps. Inactive rewards are hidden from customers."
        actions={
          <Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')} testID="reward-add">
            Add reward
          </Button>
        }
      />

      {items.length === 0 ? (
        <EmptyState
          icon={<Gift size={20} />}
          title="No rewards yet"
          subtitle="Add your first reward — Free Drink (5 stamps), Free Dessert (10), Voucher (20), etc."
          action={<Button leftIcon={<Plus size={14} />} onClick={() => setEditing('new')}>Create reward</Button>}
          testID="rewards-empty"
        />
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="rewards-grid">
          {items.map((r) => (
            <div key={r.id} data-testid={`reward-card-${r.id}`}
              className="bg-[#1A1A1A] border border-white/8 rounded-2xl overflow-hidden flex flex-col">
              <div className="relative aspect-[16/10] bg-black/40">
                {r.image_url ? (
                  <img src={resolveImageUrl(r.image_url)} alt={r.title}
                    className="absolute inset-0 w-full h-full object-cover" />
                ) : (
                  <div className="absolute inset-0 grid place-items-center text-white/30"><Gift /></div>
                )}
                <div className="absolute top-3 left-3">
                  {r.is_active === false
                    ? <StatusPill variant="slate">Inactive</StatusPill>
                    : <StatusPill variant="gold">Active</StatusPill>}
                </div>
                <div className="absolute bottom-3 right-3">
                  <StatusPill variant="gold"><Stamp size={10} /> {r.stamps_required} stamps</StatusPill>
                </div>
              </div>
              <div className="p-4 flex-1 flex flex-col gap-2">
                <div className="font-display font-semibold text-base text-white truncate">{r.title}</div>
                <p className="text-xs text-white/55 line-clamp-2 min-h-[2.25rem]">{r.description}</p>
                <div className="text-[11px] text-white/35">
                  {r.expiry_date ? `Expires ${formatDate(r.expiry_date)}` : 'No expiry'}
                </div>
                <div className="flex items-center gap-2 mt-2">
                  <Button size="sm" variant="subtle" leftIcon={<Edit size={12} />}
                    onClick={() => setEditing(r)} testID={`reward-edit-${r.id}`}>
                    Edit
                  </Button>
                  <Button size="sm" variant={r.is_active === false ? 'secondary' : 'ghost'}
                    leftIcon={<Power size={12} />}
                    onClick={() => toggle.mutate({ id: r.id, on: r.is_active === false })}
                    testID={`reward-toggle-${r.id}`}>
                    {r.is_active === false ? 'Activate' : 'Deactivate'}
                  </Button>
                  <Button size="sm" variant="danger" leftIcon={<Trash2 size={12} />}
                    onClick={async () => {
                      const ok = await confirm({
                        title: `Delete "${r.title}"?`,
                        message: 'This reward will be removed from the catalogue. Existing redemptions are preserved.',
                        confirmText: 'Delete', tone: 'danger',
                      });
                      if (ok) remove.mutate(r.id);
                    }}
                    testID={`reward-delete-${r.id}`}>
                    Delete
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing ? (
        <RewardForm
          reward={editing === 'new' ? null : editing}
          branches={branchesQ.data || []}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate(); setEditing(null); }}
        />
      ) : null}
    </div>
  );
}

function RewardForm({ reward, branches, onClose, onSaved }) {
  const token = useAuthStore((s) => s.token);
  const isEdit = !!reward;
  const [image, setImage] = useState(reward?.image_url ?? '');
  const {
    register, handleSubmit, formState: { errors, isSubmitting }, watch, setValue,
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      title: reward?.title ?? '',
      description: reward?.description ?? '',
      stamps_required: reward?.stamps_required ?? 5,
      expiry_date: reward?.expiry_date ?? '',
      terms_and_conditions: reward?.terms_and_conditions ?? '',
      available_branch_ids: reward?.available_branch_ids ?? [],
    },
  });
  const branchIds = watch('available_branch_ids');

  const onSubmit = async (vals) => {
    if (!image) { toast.error('Reward image is required'); return; }
    try {
      const body = {
        ...vals,
        image_url: image,
        expiry_date: vals.expiry_date || null,
        terms_and_conditions: vals.terms_and_conditions || null,
      };
      if (isEdit) await api.ownerPatchReward(token, reward.id, body);
      else await api.ownerCreateReward(token, body);
      toast.success(isEdit ? 'Reward updated' : 'Reward created');
      onSaved();
    } catch (e) {
      toast.error(String(e?.detail ?? 'Save failed'));
    }
  };

  return (
    <Drawer
      open
      onClose={onClose}
      title={isEdit ? 'Edit reward' : 'New reward'}
      subtitle="Customers unlock rewards by collecting stamps."
      testID="reward-drawer"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSubmit(onSubmit)} loading={isSubmitting} testID="reward-save">
            {isEdit ? 'Save changes' : 'Create reward'}
          </Button>
        </>
      }
    >
      <form className="space-y-5" onSubmit={handleSubmit(onSubmit)}>
        <FieldRow label="Reward image" required>
          <ImageUploader value={image} onChange={setImage} aspect="card" />
        </FieldRow>
        <FieldRow label="Title" required error={errors.title?.message}>
          <Input {...register('title')} placeholder="Free Drink Es Teh" />
        </FieldRow>
        <FieldRow label="Description" required error={errors.description?.message}>
          <Textarea rows={2} {...register('description')} placeholder="Free Es Teh on us — pick up at any participating branch." />
        </FieldRow>
        <div className="grid grid-cols-2 gap-3">
          <FieldRow label="Stamps required" required error={errors.stamps_required?.message}>
            <Input type="number" min={1} {...register('stamps_required')} />
          </FieldRow>
          <FieldRow label="Expiry date" hint="Leave blank for no expiry">
            <Input type="date" {...register('expiry_date')} />
          </FieldRow>
        </div>
        <FieldRow label="Terms & conditions">
          <Textarea rows={3} {...register('terms_and_conditions')} placeholder="Optional — restrictions, fine print, etc." />
        </FieldRow>
        <FieldRow label="Available at branches" hint="Leave empty to allow all branches">
          <div className="flex flex-wrap gap-2">
            {(branches || []).filter((b) => b.is_active !== false).map((b) => {
              const active = (branchIds || []).includes(b.id);
              return (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => {
                    const set = new Set(branchIds || []);
                    if (set.has(b.id)) set.delete(b.id); else set.add(b.id);
                    setValue('available_branch_ids', Array.from(set), { shouldDirty: true });
                  }}
                  className={
                    'px-3 py-1.5 rounded-lg text-xs font-medium border transition-colors ' +
                    (active
                      ? 'bg-gold/15 text-gold border-gold/30'
                      : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10')
                  }
                >
                  {b.name}
                </button>
              );
            })}
          </div>
        </FieldRow>
      </form>
    </Drawer>
  );
}
